import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosRequestConfig } from 'axios';
import { logger } from 'utils/logger';

// Create a service-specific logger
const log = logger.getLogger('ApiService');

// Types for API service
type HttpMethod = 'get' | 'post' | 'put' | 'delete';
type LoadingController = { setIsLoading: (loading: boolean) => void };

// Cache for API requests
interface CacheEntry {
    data: any;
    timestamp: number;
}

// Configuration
const CACHE_TTL = 5000; // 5 seconds cache time
const AUTH_EXEMPT_PATHS = ['/login', '/register', '/auth', '/token'];

// State management
let activeRequests = 0;
let loadingController: LoadingController | null = null;
const requestCache: Record<string, CacheEntry> = {};

/**
 * Set the loading controller from outside the service
 */
export const setLoadingController = (controller: LoadingController): void => {
    loadingController = controller;
};

/**
 * Show loading indicator and increment active request counter
 */
const showLoading = (): void => {
    activeRequests++;
    loadingController?.setIsLoading(true);
};

/**
 * Hide loading indicator when all requests are complete
 */
const hideLoading = (): void => {
    activeRequests--;
    if (activeRequests <= 0) {
        activeRequests = 0;
        loadingController?.setIsLoading(false);
    }
};

/**
 * Create a cache key from request details
 */
const createCacheKey = (method: string, url: string, data?: any): string => {
    return `${method}-${url}-${data ? JSON.stringify(data) : ''}`;
};

/**
 * Check if a URL should be exempt from authentication
 */
const isAuthExempt = (url: string | undefined): boolean => {
    if (!url) return false;
    return AUTH_EXEMPT_PATHS.some(path => url.includes(path));
};

class ApiService {
    private authService: any = null;
    private pendingRequests: Record<string, Promise<any>> = {};

    constructor() {
        this.setupInterceptors();
    }

    /**
     * Setup all axios interceptors
     */
    private setupInterceptors(): void {
        // Set default headers
        axios.defaults.headers.common['Content-Type'] = 'application/json';
        
        // Request interceptor
        axios.interceptors.request.use(
            this.handleRequestInterceptor.bind(this),
            this.handleRequestError
        );

        // Response interceptor
        axios.interceptors.response.use(
            this.handleResponseSuccess,
            this.handleResponseError
        );
    }

    /**
     * Handle request interceptor for authentication and loading state
     */
    private handleRequestInterceptor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
        showLoading();

        // Skip auth for exempt endpoints
        if (isAuthExempt(config.url)) {
            log.log(`Skipping auth for exempt endpoint: ${config.url}`);
            return config;
        }

        // Get tokens from auth service or localStorage
        const token = this.getToken();
        const idToken = this.getIdToken();
        
        // Set Authorization header if token is available
        // Always use the access token for API requests, not the ID token
        if (token && config.headers) {
            log.log(`Setting Authorization header for request to ${config.url}`);
            config.headers['Authorization'] = `Bearer ${idToken}`;
        } else {
            log.warn(`No token available for request to ${config.url}`);
        }

        return config;
    }
    /**
     * Get token from available sources
     */
    private getToken(): string | null {
        // Try to get token from auth service first
        let token = this.authService?.getToken();
        
        // Fall back to localStorage if needed
        if (!token) {
            token = localStorage.getItem('auth_token');
            if (token) {
                log.log('Retrieved token from localStorage');
            }
        } else {
            log.log('Retrieved token from authService');
        }
        
        if (!token) {
            log.warn('No token available from any source');
        }
        
        return token;
    }
    /**
     * Get ID token from available sources
     */
    private getIdToken(): string | null {
        // Try to get token from auth service first
        let idToken = this.authService?.getIdToken();
        
        // Fall back to localStorage if needed
        if (!idToken) {
            idToken = localStorage.getItem('idToken');
        }
        
        return idToken;
    }

    /**
     * Handle request error
     */
    private handleRequestError(error: any): Promise<never> {
        hideLoading();
        return Promise.reject(error);
    }

    /**
     * Handle successful response
     */
    private handleResponseSuccess(response: AxiosResponse): AxiosResponse {
        hideLoading();
        return response;
    }

    /**
     * Handle response error
     */
    private handleResponseError(error: any): Promise<never> {
        hideLoading();
        
        // Handle 401 errors
        if (error.response?.status === 401) {
            log.error(`Received 401 Unauthorized error for URL: ${error.config?.url}`);
            log.error(`Request headers: ${JSON.stringify(error.config?.headers)}`);
            // Auth0 will handle the actual logout
        }
        
        return Promise.reject(error);
    }

    /**
     * Set auth service instance from outside
     */
    setAuthService(authService: any): void {
        this.authService = authService;
        log.log('Auth service set in ApiService');
    }

    /**
     * Make a cached GET request
     */
    private async makeCachedGetRequest(url: string, data?: any): Promise<any> {
        const cacheKey = createCacheKey('get', url, data);
        const now = Date.now();

        // Return from cache if valid
        const cachedResponse = requestCache[cacheKey];
        if (cachedResponse && (now - cachedResponse.timestamp < CACHE_TTL)) {
            log.log(`Using cached response for ${url}`);
            return Promise.resolve(cachedResponse.data);
        }

        // Return pending request if one exists
        if (cacheKey in this.pendingRequests) {
            log.log(`Reusing pending request for ${url}`);
            return this.pendingRequests[cacheKey];
        }

        // Create new request
        const requestPromise = this.executeRequest('get', url, data)
            .then(response => {
                // Cache the response
                requestCache[cacheKey] = {
                    data: response.data,
                    timestamp: Date.now()
                };
                delete this.pendingRequests[cacheKey];
                return response.data;
            })
            .catch(error => {
                delete this.pendingRequests[cacheKey];
                throw error;
            });

        // Store pending request
        this.pendingRequests[cacheKey] = requestPromise;
        return requestPromise;
    }

    /**
     * Execute the actual axios request
     */
    private async executeRequest(method: HttpMethod, url: string, data?: any): Promise<AxiosResponse> {
        const config: AxiosRequestConfig = { method, url, data };
        return axios(config);
    }

    /**
     * Central method to make API requests that handles tokens and headers
     * This method should be used by all service classes
     */
    async makeRequest(method: HttpMethod, url: string, data?: any, useCache: boolean = true): Promise<any> {
        try {
            // Use caching for GET requests when enabled
            if (method === 'get' && useCache) {
                return this.makeCachedGetRequest(url, data);
            }

            // For non-GET requests or when caching is disabled
            const response = await this.executeRequest(method, url, data);
            return response.data;
        } catch (error) {
            log.error(`Error in API call to ${url}:`, error);
            throw error;
        }
    }

    /**
     * Clear the request cache - useful when logging out or when needing fresh data
     */
    clearCache(): void {
        Object.keys(requestCache).forEach(key => delete requestCache[key]);
        log.log('API request cache cleared');
    }

    /**
     * Invalidate specific cache entries by URL pattern
     */
    invalidateCacheByPattern(urlPattern: string | RegExp): void {
        const pattern = typeof urlPattern === 'string' 
            ? new RegExp(urlPattern.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'))
            : urlPattern;
            
        let invalidatedCount = 0;
        Object.keys(requestCache).forEach(key => {
            if (pattern.test(key)) {
                delete requestCache[key];
                invalidatedCount++;
            }
        });
        
        if (invalidatedCount > 0) {
            log.log(`Invalidated ${invalidatedCount} cache entries matching ${urlPattern}`);
        }
    }

    /**
     * Set the authentication token in Axios headers
     */
    setAuthorizationHeader(token: string | null): void {
        if (token) {
            log.log('Setting Authorization header with token');
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            log.warn('Clearing Authorization header');
            delete axios.defaults.headers.common['Authorization'];
        }
    }
}

const apiService = new ApiService();
export default apiService;